// Registry unico delle colonne della lista Ordini Clienti (feature 064 — issue #2929).
// Fonte di verità consumata da: selettore colonne (modale), popover ordinamento, helper
// griglia (larghezze/label) e celle (render desktop + card mobile). Porta la lista a parità
// con l'export: tutte le ~41 colonne business statiche dell'OrdineVendita, più i Campi Utente
// dinamici per organizzazione (selezionabili/visualizzabili ma NON ordinabili).
//
// INVARIANTE ORDINAMENTO (NON-NEGOTIABLE): l'ordinamento backend è riflessivo SULL'ENTITÀ
// `OrdineVendita` (ListExtensions.OrderBy via reflection) e risponde HTTP 400 su property
// inesistente, senza fallback. Quindi `sortable: true` SOLO per i campi che esistono come
// property dell'entità. `Colore` e i Campi Utente → `sortable: false`. La colonna fantasma
// `DescrizioneArticolo` (extractor nullo) è esclusa del tutto.
import { formatDate, formatNumber } from '@angular/common';
import { OrdineVendita } from './OrdineVendita';
import { CampoUtenteDef, TipoCampoUtente } from './CampoUtenteDef';
import { getStatoOrdineLabel, getStatoConsegnaLabel } from '../_helpers/stato-ordine-vendita.helper';

const LOCALE = 'it-IT';

export type OvCellKind =
  | 'text'
  | 'date'
  | 'datetime'
  | 'number'
  | 'currency'
  | 'boolean'
  | 'statoOrdine'
  | 'statoConsegna'
  | 'colore'
  | 'custom';

export interface OvListColumn {
  /** Chiave persistita in `colonneOrdiniCliente` (PascalCase; Campi Utente = `Cu_<codice>`). */
  key: string;
  /** Etichetta nel selettore e come header colonna. */
  label: string;
  /** Property di `OrdineVendita` (camelCase) per il render generico. */
  field: string;
  /** Tipo di valore/resa. `custom` = usa il `*ngSwitchCase` ricco legacy. */
  kind: OvCellKind;
  /** `true` solo se la property esiste sull'entità `OrdineVendita` (vedi invariante sopra). */
  sortable: boolean;
  /** Override del nome property per l'ordinamento backend (default = `field`). */
  sortField?: string;
  /** Larghezza CSS Grid (`minmax(...)`). */
  width: string;
  /** Categoria per il raggruppamento nel selettore. */
  category: string;
}

// ── Le 11 colonne legacy (kind 'custom') — chiavi/label/sortField/width INVARIATI per la
//    retro-compatibilità di `colonneOrdiniCliente` e la resa "ricca" esistente. ────────────
const LEGACY_COLUMNS: OvListColumn[] = [
  { key: 'RifOrdine',             label: 'N. Ordine',      field: 'rifOrdine',             kind: 'custom', sortable: true, sortField: 'rifOrdine',             width: 'minmax(110px, 0.7fr)', category: 'Riferimenti' },
  { key: 'RagioneSocialeCliente', label: 'Cliente',        field: 'ragioneSocialeCliente', kind: 'custom', sortable: true, sortField: 'ragioneSocialeCliente', width: 'minmax(170px, 1.5fr)', category: 'Cliente' },
  { key: 'StatoOrdine',           label: 'Stato Ordine',   field: 'statoOrdine',           kind: 'custom', sortable: true, sortField: 'statoOrdine',           width: 'minmax(100px, 130px)',  category: 'Stato' },
  { key: 'StatoConsegna',         label: 'Stato Consegna', field: 'statoConsegna',         kind: 'custom', sortable: true, sortField: 'statoConsegna',         width: 'minmax(110px, 140px)',  category: 'Stato' },
  { key: 'DataOrdine',            label: 'Data Ordine',    field: 'dataOrdine',            kind: 'custom', sortable: true, sortField: 'dataOrdine',            width: 'minmax(95px, 110px)',   category: 'Date' },
  { key: 'DataConsegna',          label: 'Data Consegna',  field: 'dataConsegnaRiga',      kind: 'custom', sortable: true, sortField: 'dataConsegnaRiga',      width: 'minmax(100px, 120px)',  category: 'Date' },
  { key: 'CodiceArticolo',        label: 'Articolo',       field: 'codiceArticolo',        kind: 'custom', sortable: true, sortField: 'codiceArticolo',        width: 'minmax(150px, 1.4fr)', category: 'Articolo' },
  { key: 'Commessa',              label: 'Commessa',       field: 'commessa',              kind: 'custom', sortable: true, sortField: 'commessa',              width: 'minmax(110px, 1fr)',   category: 'Riferimenti' },
  { key: 'Zona',                  label: 'Zona',           field: 'zona',                  kind: 'custom', sortable: true, sortField: 'zona',                  width: 'minmax(90px, 1fr)',    category: 'Consegna' },
  { key: 'Rappresentante',        label: 'Rappresentante', field: 'rappresentante',        kind: 'custom', sortable: true, sortField: 'rappresentante',        width: 'minmax(130px, 1fr)',   category: 'Cliente' },
  { key: 'UdM',                   label: 'UdM',            field: 'quantita',              kind: 'custom', sortable: true, sortField: 'quantita',              width: 'minmax(85px, 105px)',  category: 'Quantità/Peso' },
];

// ── Nuove colonne statiche (derivate dall'export, esclusi audit e la colonna fantasma
//    `DescrizioneArticolo`). `sortable` esplicito per riga: true per le property entity-backed,
//    false solo per `Colore`. ───────────────────────────────────────────────────────────────
const NEW_COLUMNS: OvListColumn[] = [
  // Riferimenti
  { key: 'NumRegistrazione',  label: 'N° Registrazione',      field: 'numRegistrazione',  kind: 'number',   sortable: true,  width: 'minmax(110px, 1fr)',  category: 'Riferimenti' },
  { key: 'AnnoOrdine',        label: 'Anno Ordine',           field: 'annoOrdine',        kind: 'number',   sortable: true,  width: 'minmax(90px, 110px)', category: 'Riferimenti' },
  // Feature 067 — revisione "ordine bis"; vuota per il base. Bis è property reale dell'entità → ordinabile.
  { key: 'Bis',               label: 'Rev.',                  field: 'bis',               kind: 'number',   sortable: true,  width: 'minmax(70px, 90px)',  category: 'Riferimenti' },
  { key: 'RifCliente',        label: 'Rif. Cliente',          field: 'rifCliente',        kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Riferimenti' },
  // Cliente
  { key: 'AnagraficaId',      label: 'ID Anagrafica',         field: 'anagraficaId',      kind: 'text',     sortable: true,  width: 'minmax(100px, 120px)', category: 'Cliente' },
  // Feature 094 (issue #3118): Nota cliente (sort riflessivo su field, filtro testo + per valori; niente raggruppamento).
  { key: 'NotaCliente',       label: 'Nota cliente',          field: 'notaCliente',       kind: 'text',     sortable: true,  width: 'minmax(160px, 1.5fr)', category: 'Cliente' },
  // Feature 101 (issue #3175): Nota consegna (sort riflessivo su field, filtro testo + per valori; niente raggruppamento).
  { key: 'NotaConsegna',      label: 'Nota consegna',         field: 'notaConsegna',      kind: 'text',     sortable: true,  width: 'minmax(160px, 1.5fr)', category: 'Cliente' },
  { key: 'NotaAggiuntiva',    label: 'Nota aggiuntiva',       field: 'notaAggiuntiva',    kind: 'text',     sortable: true,  width: 'minmax(160px, 1.5fr)', category: 'Cliente' },
  // Articolo
  { key: 'VarianteArt',       label: 'Variante Articolo',     field: 'varianteArt',       kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Articolo' },
  { key: 'ArticoloId',        label: 'ID Articolo',           field: 'articoloId',        kind: 'text',     sortable: true,  width: 'minmax(100px, 120px)', category: 'Articolo' },
  { key: 'VarianteId',        label: 'ID Variante',           field: 'varianteId',        kind: 'text',     sortable: true,  width: 'minmax(100px, 120px)', category: 'Articolo' },
  { key: 'RifLottoProdotto',  label: 'Lotto Prodotto',        field: 'rifLottoProdotto',  kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Articolo' },
  { key: 'CodiceMateriale',   label: 'Codice Materiale',      field: 'codiceMateriale',   kind: 'text',     sortable: true,  width: 'minmax(140px, 1fr)',  category: 'Articolo' },
  { key: 'VarianteMat',       label: 'Variante Materiale',    field: 'varianteMat',       kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Articolo' },
  { key: 'RifLottoMateriale', label: 'Lotto Materiale',       field: 'rifLottoMateriale', kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Articolo' },
  // Consegna
  { key: 'IndirizzoConsegna', label: 'Indirizzo Consegna',    field: 'indirizzoConsegna', kind: 'text',     sortable: true,  width: 'minmax(160px, 1.5fr)', category: 'Consegna' },
  { key: 'Citta',             label: 'Città',                 field: 'citta',             kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Consegna' },
  { key: 'CAP',               label: 'CAP',                   field: 'cap',               kind: 'text',     sortable: true,  width: 'minmax(80px, 100px)', category: 'Consegna' },
  { key: 'Provincia',         label: 'Provincia',             field: 'provincia',         kind: 'text',     sortable: true,  width: 'minmax(90px, 110px)', category: 'Consegna' },
  { key: 'Nazione',           label: 'Nazione',               field: 'nazione',           kind: 'text',     sortable: true,  width: 'minmax(90px, 120px)', category: 'Consegna' },
  { key: 'SedeConsegnaId',    label: 'ID Sede Consegna',      field: 'sedeConsegnaId',    kind: 'text',     sortable: true,  width: 'minmax(100px, 120px)', category: 'Consegna' },
  // Feature 079: contatti sede di consegna.
  { key: 'Riferimento',       label: 'Riferimento Consegna',  field: 'riferimento',       kind: 'text',     sortable: true,  width: 'minmax(140px, 1.2fr)', category: 'Consegna' },
  { key: 'Telefono',          label: 'Telefono',              field: 'telefono',          kind: 'text',     sortable: true,  width: 'minmax(110px, 130px)', category: 'Consegna' },
  { key: 'Email',             label: 'Email',                 field: 'email',             kind: 'text',     sortable: true,  width: 'minmax(160px, 1.5fr)', category: 'Consegna' },
  // Quantità/Peso
  { key: 'QuantitaUdM2',      label: 'Quantità UdM2',         field: 'quantitaUdM2',      kind: 'number',   sortable: true,  width: 'minmax(100px, 120px)', category: 'Quantità/Peso' },
  { key: 'UdM2',              label: 'UdM2',                  field: 'udM2',              kind: 'text',     sortable: true,  width: 'minmax(85px, 105px)', category: 'Quantità/Peso' },
  { key: 'MagUdM',            label: 'UdM Magazzino',         field: 'magUdM',            kind: 'text',     sortable: true,  width: 'minmax(100px, 120px)', category: 'Quantità/Peso' },
  { key: 'PesoNetto',         label: 'Peso Netto',            field: 'pesoNetto',         kind: 'number',   sortable: true,  width: 'minmax(100px, 120px)', category: 'Quantità/Peso' },
  { key: 'PesoLordo',         label: 'Peso Lordo',            field: 'pesoLordo',         kind: 'number',   sortable: true,  width: 'minmax(100px, 120px)', category: 'Quantità/Peso' },
  // Magazzino
  { key: 'CodMagPrincipale',  label: 'Magazzino Principale',  field: 'codMagPrincipale',  kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Magazzino' },
  { key: 'CodAreaMagPrinc',   label: 'Area Magazzino Princ.', field: 'codAreaMagPrinc',   kind: 'text',     sortable: true,  width: 'minmax(120px, 1fr)',  category: 'Magazzino' },
  // Importi
  { key: 'ImportoRiga',       label: 'Importo Riga',          field: 'importoRiga',       kind: 'currency', sortable: true,  width: 'minmax(110px, 130px)', category: 'Importi' },
  { key: 'Valuta',            label: 'Valuta',                field: 'valuta',            kind: 'text',     sortable: true,  width: 'minmax(80px, 100px)', category: 'Importi' },
  // Date
  { key: 'DataRegistrazione', label: 'Data Registrazione',    field: 'dataRegistrazione', kind: 'date',     sortable: true,  width: 'minmax(110px, 130px)', category: 'Date' },
  // Feature 112 (issue #3309): date di audit. `sortable: true` è lecito — DataCreazione e
  // DataModifica sono property reali dell'entità OrdineVendita (invariante in testa al file).
  // Escluse dal filtro "per valori" (OV_KEYS_SENZA_FILTRO_VALORI) e dal raggruppamento: un istante
  // al minuto è distinto quasi per ogni riga.
  { key: 'DataCreazione',     label: 'Data creazione',        field: 'dataCreazione',     kind: 'datetime', sortable: true,  width: 'minmax(130px, 150px)', category: 'Date' },
  { key: 'DataModifica',      label: 'Data ultima modifica',  field: 'dataModifica',      kind: 'datetime', sortable: true,  width: 'minmax(130px, 150px)', category: 'Date' },
  // Stato
  { key: 'FlgTerminato',      label: 'Terminato',             field: 'flgTerminato',      kind: 'boolean',  sortable: true,  width: 'minmax(90px, 110px)', category: 'Stato' },
  // Note
  { key: 'Note',              label: 'Note',                  field: 'note',              kind: 'text',     sortable: true,  width: 'minmax(160px, 1.5fr)', category: 'Note' },
  // Colore (feature 060) — NON ordinabile (stringa esadecimale priva di senso d'ordine).
  { key: 'Colore',            label: 'Colore',                field: 'colore',            kind: 'colore',   sortable: false, width: 'minmax(70px, 90px)',  category: 'Colore' },
];

/** Tutte le colonne statiche (legacy + nuove), nell'ordine canonico del registry. */
export const OV_LIST_COLUMNS: OvListColumn[] = [...LEGACY_COLUMNS, ...NEW_COLUMNS];

/** Lookup O(1) per chiave colonna statica. */
export const OV_LIST_COLUMN_MAP: Map<string, OvListColumn> =
  new Map(OV_LIST_COLUMNS.map(c => [c.key, c]));

/**
 * Colonne escluse dal modale di filtro "per valori" (feature 112, issue #3309).
 *
 * - `Colore`: esclusione preesistente (stringa esadecimale, elenco privo di senso).
 * - `DataCreazione` / `DataModifica`: un istante al minuto è distinto quasi per ogni riga, quindi
 *   l'elenco dei valori selezionabili sarebbe lungo quanto il dataset; inoltre la richiesta di
 *   valori distinti su queste chiavi cadrebbe fuori dalla whitelist server-side, con errore visibile.
 */
export const OV_KEYS_SENZA_FILTRO_VALORI = new Set(['Colore', 'DataCreazione', 'DataModifica']);

/** Ritorna la definizione statica della colonna, o `undefined` (es. chiave `Cu_<codice>`). */
export function getOvColumnMeta(key: string): OvListColumn | undefined {
  return OV_LIST_COLUMN_MAP.get(key);
}

/**
 * Risolve la definizione di una colonna — statica (dal registry) OPPURE dinamica
 * `Cu_<codice>` (ricostruita dalle definizioni Campi Utente attive). I `Cu_<codice>` non sono
 * presenti in `OV_LIST_COLUMN_MAP` (sono per organizzazione), quindi `getOvColumnMeta` da solo
 * ritornerebbe `undefined`: usare questa funzione ovunque serva la meta di una colonna
 * eventualmente dinamica (render celle, etichette header). Ritorna `undefined` se la chiave
 * non è né statica né un Campo Utente attivo.
 */
export function resolveOvColumn(
  key: string,
  campiUtenteDefs?: CampoUtenteDef[]
): OvListColumn | undefined {
  if (key.startsWith('Cu_')) {
    return buildCampiUtenteColumns(campiUtenteDefs ?? []).find(c => c.key === key);
  }
  return OV_LIST_COLUMN_MAP.get(key);
}

/** Etichetta di una colonna (statica o `Cu_<codice>`); fallback alla chiave grezza. */
export function getOvColumnLabel(key: string, campiUtenteDefs?: CampoUtenteDef[]): string {
  return resolveOvColumn(key, campiUtenteDefs)?.label ?? key;
}

/**
 * Opzioni del popover di ordinamento: solo colonne `sortable === true`, deduplicate per
 * `sortField` (la chiave inviata al backend), nell'ordine del registry. Garantisce FR-013:
 * ogni opzione corrisponde a una property dell'entità `OrdineVendita` → nessun HTTP 400.
 */
export function buildSortOptionsFromRegistry(): { key: string; label: string }[] {
  const out: { key: string; label: string }[] = [];
  const seen = new Set<string>();
  for (const c of OV_LIST_COLUMNS) {
    if (!c.sortable) continue;
    const key = c.sortField ?? c.field;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ key, label: c.label });
  }
  return out;
}

/** Genera le colonne dinamiche `Cu_<codice>` dalle definizioni Campi Utente attive (non ordinabili). */
export function buildCampiUtenteColumns(definizioniAttive: CampoUtenteDef[]): OvListColumn[] {
  return (definizioniAttive ?? [])
    .filter(d => d.isActive)
    .map(d => ({
      key: `Cu_${d.codice}`,
      label: d.etichetta || d.codice,
      field: 'campiUtente',
      kind: d.tipoCampo === TipoCampoUtente.Numero ? 'number' as const : 'text' as const,
      sortable: false,
      width: 'minmax(120px, 1fr)',
      category: 'Campi Utente',
    }));
}

// ── Formattazione per tipo (funzioni dedicate, no switch annidato profondo) ─────────────────

const PLACEHOLDER = '-';

function formatDateValue(raw: unknown): string {
  return raw ? formatDate(raw as string | number | Date, 'dd/MM/yyyy', LOCALE) : PLACEHOLDER;
}

// Feature 112 (issue #3309) — celle `datetime` (date di audit).
// Divergenza deliberata con l'export: qui il valore assente rende il PLACEHOLDER '-' come tutte le
// altre colonne data della lista (FR-014); nell'export rende una cella VUOTA (FR-022), perché un
// segnaposto in un foglio di calcolo sporcherebbe filtri e ordinamenti. Non uniformare i due canali.
function formatDateTimeValue(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return PLACEHOLDER;

  // Valore-sentinella 0001-01-01: righe non ancora allineate dal backfill → mai una data del
  // primo secolo in faccia all'utente.
  if (typeof raw === 'string' && raw.startsWith('0001-')) return PLACEHOLDER;

  // L'API serializza gli istanti UTC SENZA suffisso 'Z': senza forzarlo il browser li leggerebbe
  // come ora locale, sbagliando di 1–2 ore secondo l'ora legale (stessa logica di UtcToLocalPipe).
  const date = raw instanceof Date
    ? raw
    : new Date(/[zZ]|[+-]\d{2}:\d{2}$/.test(String(raw)) ? String(raw) : `${String(raw)}Z`);

  if (isNaN(date.getTime())) return PLACEHOLDER;
  return formatDate(date, 'dd/MM/yyyy HH:mm', LOCALE);
}

function formatNumberValue(raw: unknown): string {
  const n = Number(raw);
  return Number.isFinite(n) ? formatNumber(n, LOCALE, '1.0-3') : PLACEHOLDER;
}

function formatCurrencyValue(raw: unknown): string {
  const n = Number(raw);
  return Number.isFinite(n) ? formatNumber(n, LOCALE, '1.2-2') : PLACEHOLDER;
}

function formatBooleanValue(raw: unknown): string {
  return raw ? 'Sì' : 'No';
}

const KIND_FORMATTERS: Partial<Record<OvCellKind, (raw: unknown) => string>> = {
  date: formatDateValue,
  datetime: formatDateTimeValue,
  number: formatNumberValue,
  currency: formatCurrencyValue,
  boolean: formatBooleanValue,
};

function parseCampiUtente(json: string | null | undefined): Record<string, string> {
  if (!json) return {};
  try {
    const obj = JSON.parse(json) as Record<string, unknown>;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(obj ?? {})) {
      if (v !== null && v !== undefined) out[k] = String(v);
    }
    return out;
  } catch {
    return {};
  }
}

function formatCampoUtente(ordine: OrdineVendita, meta: OvListColumn, defs?: CampoUtenteDef[]): string {
  const codice = meta.key.slice(3); // rimuove il prefisso 'Cu_'
  const def = (defs ?? []).find(d => d.codice === codice && d.isActive);
  if (!def) return PLACEHOLDER; // FR-012: Campo Utente non più attivo → ignorato senza errori
  const valore = parseCampiUtente(ordine.campiUtente)[codice];
  if (valore === undefined || valore === '') return PLACEHOLDER;
  if (def.tipoCampo === TipoCampoUtente.Booleano) return valore.toLowerCase() === 'true' ? 'Sì' : 'No';
  if (def.tipoCampo === TipoCampoUtente.Numero) return formatNumberValue(valore);
  return valore;
}

/**
 * Valore formattato di una cella per il render generico (desktop + mobile), guidato dal `kind`.
 * Le colonne `Cu_<codice>` leggono il valore dal JSON `campiUtente` per `tipoCampo`.
 * `colore` ha una resa dedicata (swatch) nel template, quindi qui ritorna il valore grezzo.
 */
export function formatOvCellValue(
  ordine: OrdineVendita,
  meta: OvListColumn,
  campiUtenteDefs?: CampoUtenteDef[]
): string {
  if (meta.key.startsWith('Cu_')) return formatCampoUtente(ordine, meta, campiUtenteDefs);
  const raw = (ordine as unknown as Record<string, unknown>)[meta.field];
  if (raw === null || raw === undefined || raw === '') return PLACEHOLDER;
  const formatter = KIND_FORMATTERS[meta.kind];
  return formatter ? formatter(raw) : String(raw);
}

/**
 * Etichetta localizzata di un valore "raw" invariante del filtro per valori (issue #3115),
 * PRESERVANDO il valore raw da inviare al backend. Formati:
 * - date `yyyy-MM-dd` → `dd/MM/yyyy` per SPLIT stringa (nessun `new Date()`, nessun shift di fuso);
 * - stati → label italiana esistente (`meta.key` StatoOrdine/StatoConsegna);
 * - numeri/valute → formato it-IT; booleani → Sì/No; testo → invariato.
 */
export function formatValoreColonna(meta: OvListColumn | undefined, raw: string): string {
  if (raw === '' || raw === null || raw === undefined) return raw;
  if (meta?.key === 'StatoOrdine') return getStatoOrdineLabel(Number(raw));
  if (meta?.key === 'StatoConsegna') return getStatoConsegnaLabel(Number(raw));
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-');
    return `${d}/${m}/${y}`;
  }
  if (meta?.kind === 'boolean') return raw === 'true' ? 'Sì' : 'No';
  if (meta?.kind === 'number' || meta?.kind === 'currency') {
    const n = Number(raw);
    return Number.isFinite(n) ? formatNumber(n, LOCALE, meta.kind === 'currency' ? '1.2-2' : '1.0-3') : raw;
  }
  return raw;
}
